import { useEffect, useState } from "react";
import { Alert, Spin, Tag, Typography } from "antd";
import { getCompanyDescendants } from "../repository/data.repository";
import { Company } from "../repository/data.default";

export interface PolicyAccessPreviewProps {
  // The policy's scope company. Undefined ⇒ platform-wide (every company).
  readonly scopeCompanyUid?: string;
}

/**
 * Shows which companies would have access to a policy given its scope: a platform-wide policy reaches every
 * company; a company-scoped policy reaches that company plus all of its sub-companies (inheritance is downward).
 * Mirrors the backend read scope, so what the admin sees here is exactly what those companies will see.
 */
function PolicyAccessPreview({ scopeCompanyUid }: PolicyAccessPreviewProps) {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    if (!scopeCompanyUid) {
      setCompanies([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getCompanyDescendants(scopeCompanyUid)
      .then((data) => {
        if (!cancelled) setCompanies(data);
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeCompanyUid]);

  if (!scopeCompanyUid) {
    return (
      <Alert
        className="mt-2"
        type="info"
        showIcon
        message="Visible to all companies"
        description="A platform-wide policy is inherited by every company on the platform."
      />
    );
  }

  const root = companies.find((c) => c.uid === scopeCompanyUid);
  const subCount = Math.max(companies.length - 1, 0);
  const summary =
    subCount > 0
      ? `Visible to ${root?.name ?? "this company"} and ${subCount} sub-compan${subCount === 1 ? "y" : "ies"}`
      : `Visible to ${root?.name ?? "this company"} only`;

  return (
    <div className="mt-2">
      <Typography.Text type="secondary">
        {loading ? "Resolving companies with access…" : summary}
      </Typography.Text>
      {loading ? (
        <div className="mt-1">
          <Spin size="small" />
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap gap-1">
          {companies.map((company) => (
            <Tag key={company.uid} color={company.uid === scopeCompanyUid ? "blue" : undefined}>
              {company.name}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

export default PolicyAccessPreview;
